import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { Keys } from '@holmescorp/common';
import { v, w } from '@dojo/framework/core/vdom';
import { DNode, LocalizedMessages } from '@dojo/framework/core/interfaces';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import Store from '@dojo/framework/stores/Store';
import { Bundle, Messages } from '@dojo/framework/i18n/i18n';

import { TopicAttrs, UserMeta, ParentTree } from 'lcc-state/interfaces';
import { keymap, pathmap, SiteMeta } from 'lcc-state';
import { numeral } from '@holmescorp/numeral';

import { Circle, Line } from 'progress-bar';

import { TopicSet } from './topicset';
import { TopicProperties } from '../interfaces';
import css from '../css/topichead.m.css';

interface StatusProperties {
	status?: ParentTree;
	assessment?: ParentTree;
	messages: Messages;
}

@theme(css)
@storeInject({
	name: 'user-state',
	paths: [['status'], ['assessment']],
	getProperties: (store: Store<UserMeta>) => {
		const { get, path } = store;
		return {
			status: get(path('status')),
			assessment: get(path('assessment'))
		} as StatusProperties;
	}
})
class Status extends I18nThemed<StatusProperties> {
	protected render(): DNode {
		const { key = '', status, assessment, messages } = this.properties;
		const proficiency = messages['your_proficiency'] || '';
		const progress = messages['your_progress'] || '';
		if (status === undefined || status[key] === undefined) {
			const legendPercent = numeral(0).format('0%');
			return v('div', { key: `${key}-status`, classes: [this.theme(css.status)] }, [
				w(Circle, { key: `${key}-1`, progress: 0, legend: legendPercent, title: proficiency }),
				w(Line, { key: `${key}-2`, progress: 0, total: 0, title: progress })
			]);
		} else {
			const { complete, total, percent } = status[key as string].data;
			const assess = assessment ? assessment[key as string] : { data: { percent: 0 } };
			const { percent: innerPercent = 0 } = assess.data;

			const legendPercent = numeral(percent).format('0%');

			return v('div', { key: `${key}-status`, classes: [this.theme(css.status)] }, [
				w(Circle, {
					key: `${key}-1`,
					progress: percent * 100,
					inner: +innerPercent,
					legend: legendPercent,
					title: proficiency
				}),
				w(Line, { key: `${key}-2`, progress: complete, total, title: progress })
			]);
		}
	}
}

export interface TopicHeadProperties extends TopicProperties {
	dynls?: Map<string, Bundle<Messages>>;
}

@theme(css)
@storeInject({
	name: 'lcc-state',
	paths: [['dynls']],
	getProperties: (store: Store<SiteMeta>) => {
		const { get, path } = store;
		return {
			dynls: get(path('dynls'))
		};
	}
})
export class TopicHead extends I18nThemed<TopicHeadProperties> {
	private _firstSet: string | undefined = undefined;

	private _selected() {
		if (this._firstSet) {
			const { path } = keymap.get(this._firstSet)!;
			const { view } = pathmap.get(path)!;
			view();
		}
	}

	private _onKeyDown(event: KeyboardEvent) {
		event.stopPropagation();

		switch (event.key) {
			case Keys.Enter:
			case Keys.Space:
			case Keys.Spacebar:
				event.preventDefault();
				this._selected();
				break;
		}
	}

	protected get_nls(): LocalizedMessages<Messages> {
		const { dynls } = this.properties;
		const bundle = dynls!.get('ditabook');
		if (!bundle) {
			return { isPlaceholder: true, format: () => '', messages: {} as Messages };
		}
		return this.localizeBundle(bundle);
	}

	protected render(): DNode {
		const { isPlaceholder, messages } = this.get_nls();
		if (isPlaceholder) {
			return null;
		}
		const {
			topic: { attributes = {} as TopicAttrs, children = [] }
		} = this.properties;
		const { topic } = this.properties;
		const { title, key } = attributes;

		const kids = children.map((val, idx) => {
			const { attributes, children = [] } = val['topicset'];
			if (idx === 0) {
				this._firstSet = attributes!.key;
			}
			return w(TopicSet, { topic: { attributes, children }, topichead: topic });
		});

		return v(
			'div',
			{
				key,
				tabIndex: 0,
				classes: [this.theme(css.root), key],
				xqa: `topichead ${key}`,
				onclick: (event) => {
					event && event.stopImmediatePropagation();
					this._selected();
				},
				onkeydown: this._onKeyDown
			},
			[v('div', { classes: [this.theme(css.title)] }, [title]), w(Status, { key, messages }), ...kids]
		);
	}
}
